import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Star, Download, Share, Heart, MoreVertical, ChevronRight } from 'lucide-react';
import { appData } from '../data/mock';

const AppDetailPage = () => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  
  const handleInstallClick = async () => {
    // Meta Pixel tracking
    if (window.fbq) {
      window.fbq('track', 'Install');
    }
    
    // Check if PWA install is supported
    if (window.deferredPrompt) {
      const deferredPrompt = window.deferredPrompt;
      deferredPrompt.prompt();
      
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      
      // Clear the deferredPrompt
      window.deferredPrompt = null;
    } else if (navigator.standalone !== undefined) {
      // iOS Safari - show installation instructions
      alert('To install this app on your iOS device, tap the Share button and then "Add to Home Screen".');
    } else {
      // Fallback: redirect to loading page
      window.location.href = '/';
    }
  };
  
  const handleOpenClick = () => {
    window.location.href = '/';
  };
  
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };
  
  const renderRatingBar = (stars, percentage) => (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-2">{stars}</span>
      <div className="flex-1 bg-gray-200 rounded-full h-2">
        <div 
          className="bg-gray-400 h-2 rounded-full" 
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <h1 className="text-lg font-semibold text-green-600">Google Play</h1>
          <MoreVertical className="w-6 h-6 text-gray-600" />
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* App Header Section */}
        <div className="flex gap-4 mb-6">
          <img 
            src={appData.icon} 
            alt={appData.name}
            className="w-24 h-24 rounded-xl border border-gray-200"
          />
          <div className="flex-1">
            <h1 className="text-2xl font-normal text-gray-900 mb-1">
              {appData.name}
            </h1>
            <p className="text-green-700 text-sm mb-1">{appData.developer}</p>
            <div className="flex items-center gap-2 mb-2">
              {appData.labels.map((label, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {label}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        
        {/* Rating and Stats */}
        <div className="flex items-center gap-6 mb-6 text-sm">
          <div className="text-center">
            <div className="text-2xl font-normal">{appData.rating}</div>
            <div className="flex justify-center mb-1">
              {renderStars(appData.rating)}
            </div>
            <div className="text-gray-600">{appData.reviews}</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-normal">{appData.downloads}</div>
            <div className="text-gray-600">Downloads</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-normal">🔞</div>
            <div className="text-gray-600">PEGI 18 ⓘ</div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-3 mb-8">
          <Button 
            onClick={handleInstallClick}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-medium"
          >
            <Download className="w-4 h-4 mr-2" />
            Install
          </Button>
          <Button 
            variant="outline" 
            onClick={handleOpenClick}
            className="px-6 py-3 rounded-lg"
          >
            Open
          </Button>
          <Button variant="ghost" size="icon" className="rounded-lg">
            <Share className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" className="rounded-lg">
            <Heart className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Ready to download notice */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
          <p className="text-green-800 text-sm font-medium">Ready to download</p>
        </div>
        
        {/* Screenshots */}
        <div className="mb-8">
          <div className="flex gap-3 overflow-x-auto pb-3">
            {appData.screenshots.map((screenshot, index) => (
              <div key={index} className="flex-shrink-0 relative">
                <img 
                  src={screenshot} 
                  alt={`Screenshot ${index + 1}`}
                  className="w-48 h-80 rounded-lg border border-gray-200 object-cover"
                />
              </div>
            ))}
          </div>
        </div>
        
        {/* About Section */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-medium mb-4">About this app</h2>
            <div className="text-gray-700 leading-relaxed">
              <p className="mb-4">{appData.aboutDescription}</p>
              
              {showFullDescription && (
                <div>
                  <h3 className="font-medium mb-2">Key Features:</h3>
                  <ul className="list-disc list-inside space-y-1 mb-4">
                    {appData.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <Button 
                variant="ghost" 
                className="text-green-600 p-0 h-auto font-medium"
                onClick={() => setShowFullDescription(!showFullDescription)}
              >
                {showFullDescription ? 'Read less' : 'Read more'}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Ratings and Reviews */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-medium mb-4">Ratings and reviews</h2>
            
            <div className="flex gap-8 mb-6">
              <div className="text-center">
                <div className="text-4xl font-normal mb-2">{appData.rating}</div>
                <div className="flex justify-center mb-2">
                  {renderStars(appData.rating)}
                </div>
                <div className="text-gray-600 text-sm">{appData.reviews}</div>
              </div>
              
              <div className="flex-1 space-y-1">
                {Object.entries(appData.ratingDistribution)
                  .reverse()
                  .map(([stars, percentage]) => renderRatingBar(stars, percentage))}
              </div>
            </div>
            
            <div className="space-y-4">
              {appData.userReviews.map((review) => (
                <div key={review.id} className="flex gap-3">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-gray-200 text-gray-600 text-sm">
                      {review.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{review.author}</span>
                      <span className="text-gray-500 text-xs">{review.date}</span>
                    </div>
                    <div className="flex mb-2">
                      {renderStars(review.rating)}
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{review.text}</p>
                    <div className="flex gap-4 mt-2">
                      <Button variant="ghost" size="sm" className="text-xs text-gray-600 p-0 h-auto">
                        Helpful
                      </Button>
                      <Button variant="ghost" size="sm" className="text-xs text-gray-600 p-0 h-auto">
                        Flag as inappropriate
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* 18+ Warning */}
        <Card className="mb-8 border-red-200 bg-red-50">
          <CardContent className="p-6">
            <h3 className="font-medium text-red-800 mb-2">18+ Content Warning</h3>
            <p className="text-red-700 text-sm leading-relaxed">
              This application contains adult content and is intended for users 18 years and older only.
              By installing this app, you confirm that you are of legal age in your jurisdiction and consent to viewing adult content.
            </p>
          </CardContent>
        </Card>
        
        {/* Footer Links */}
        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
          <a href="#" className="hover:text-gray-800">Privacy Policy</a>
          <a href="#" className="hover:text-gray-800">Terms of Service</a>
          <a href="#" className="hover:text-gray-800">Content Guidelines</a>
        </div>
        
        <p className="text-gray-500 text-xs">© 2025 Google LLC</p>
      </div>
    </div>
  );
};

export default AppDetailPage;